// cloudAdapter.js — StageBoss Cloud data adapter
// Replaces blob-era cloudFetch/cloudSave. Reads/writes the stageboss-cloud
// normalized tables but presents App.js with the EXACT blob shape it expects:
// { venues: [...], tours: [...], comedians: [...], templates: [...] }
//
// Lossless strategy: every parent row stores its full original object in the
// `extra` jsonb column. Mapped columns are also written (queryable for SaaS).
// On load: object = camelized(columns) overlaid with extra (extra wins),
// then nested children (contactLog, dates, bookouts) are attached for rows
// whose extra is empty (pre-adapter migrated rows).

import { createClient } from '@supabase/supabase-js';

export const CLOUD_URL = 'https://xljalidtovgdzdqhvcrk.supabase.co';
export const CLOUD_KEY = 'sb_publishable_T5SDWUwJ5GoDhsjraeypmA_xq4TsWfM';

let _client = null;
export function cloudClient() {
  if (!_client) _client = createClient(CLOUD_URL, CLOUD_KEY);
  return _client;
}
// Allow tests / App.js to inject an existing client
export function _setClient(c) { _client = c; }

// ---------- key mapping ----------
const snake = (k) => k.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
const camel = (k) => k.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

// column -> blob key, where camelization alone isn't enough
const VENUE_COL_TO_KEY = {
  legacy_id: 'id', venue_type: 'venueType', calendar_url: 'calendarUrl',
  prior_friction: 'priorFriction', re_engage_flag: 'reEngageFlag',
  referral_source: 'referralSource', routing_cluster: 'routingCluster',
  seasonal_window: 'seasonalWindow', trust_level: 'trustLevel',
};
const DEAL_COL_TO_KEY = {
  agent_commission: 'agentCommission', agreement_type: 'agreementType',
  balance_due: 'balanceDue', balance_paid: 'balancePaid',
  close_probability: 'closeProbability', closed_by: 'dealClosedBy',
  contract_status: 'contractStatus', contract_url: 'contractUrl',
  deal_type: 'dealType', deposit_amount: 'depositAmount',
  deposit_due: 'depositDue', deposit_paid: 'depositPaid',
  door_split: 'doorSplit', expected_payment_timing: 'expectedPaymentTiming',
  manager_commission: 'managerCommission', marketing_support: 'marketingSupport',
  merch_allowed: 'merchAllowed', merch_cut: 'merchCut',
  paid_date: 'paidDate', radius_clause: 'radiusClause',
  show_count: 'showCount', terms_locked: 'termsLocked',
  ticket_price: 'ticketPrice',
};
const OUTREACH_COL_TO_KEY = {
  ab_variant: 'abVariant', next_follow_up: 'nextFollowUp',
  thread_url: 'emailThreadURL', thread_text: 'emailThreadText',
  email_open_last: 'emailOpenLastDate', last_email_sent: 'lastEmailSentDate',
  last_response: 'lastResponseDate', email_open_count: 'emailOpenCount',
};
const TOUR_COL_TO_KEY = {
  legacy_id: 'id', lodging_budget: 'lodgingBudget', misc_budget: 'miscBudget',
  start_date: 'startDate', end_date: 'endDate', travel_budget: 'travelBudget',
};
const PERFORMER_COL_TO_KEY = { legacy_id: 'id', default_fee: 'defaultFee' };
const TEMPLATE_COL_TO_KEY = {
  legacy_id: 'id', photo_links: 'photoLinks', touch_number: 'touchNumber',
  venue_types: 'venueTypes',
};
const SHOW_COL_TO_KEY = { show_date: 'date' };
const BOOKOUT_COL_TO_KEY = { start_date: 'startDate', end_date: 'endDate' };

const DB_ONLY = new Set(['user_id', 'created_at', 'extra', 'venue_id', 'tour_id', 'performer_id', 'show_id', 'website_id']);

function idFromLegacy(lid) {
  if (lid === null || lid === undefined) return lid;
  return /^\d+$/.test(String(lid)) ? Number(lid) : lid;
}

// Build blob object from a table row: mapped columns -> original keys, then extra overlays.
function rowToObj(row, colMap) {
  const o = {};
  for (const [col, val] of Object.entries(row)) {
    if (DB_ONLY.has(col) || col === 'id') continue;
    if (val === null || val === undefined) continue;
    const key = colMap[col] || camel(col);
    o[key] = col === 'legacy_id' ? idFromLegacy(val) : val;
  }
  const extra = row.extra && typeof row.extra === 'object' ? row.extra : {};
  return { ...o, ...extra };
}

// Split a blob object into { cols, extra } for a table.
// knownCols: Set of real column names. colToKey: column->blobKey map.
function objToRow(obj, knownCols, colToKey, skipKeys = new Set()) {
  const keyToCol = {};
  for (const [c, k] of Object.entries(colToKey)) keyToCol[k] = c;
  const cols = {};
  const extra = {};
  for (const [k, v] of Object.entries(obj)) {
    if (skipKeys.has(k)) continue;
    const col = keyToCol[k] || snake(k);
    if (knownCols.has(col) && col !== 'extra') {
      cols[col] = col === 'legacy_id' ? String(v) : (v === '' ? null : v); // '' -> null: Postgres rejects '' for numeric/bool/date (22P02)
    } else {
      extra[k] = v;
    }
  }
  return { cols, extra };
}

// Real column sets (from live schema recon, minus id/user_id which we set explicitly)
const VENUE_COLS = new Set(['address','calendar_url','capacity','city','history','instagram','legacy_id','name','notes','prior_friction','re_engage_flag','referral_source','relationship','routing_cluster','seasonal_window','state','status','trust_level','venue_type','warmth','website','zip','extra']);
const DEAL_COLS = new Set(['agent_commission','agreement_type','balance_due','balance_paid','close_probability','closed_by','contract_status','contract_url','deal_type','deposit_amount','deposit_due','deposit_paid','door_split','expected_payment_timing','guarantee','lodging','manager_commission','marketing_support','merch_allowed','merch_cut','notes','package','paid','paid_date','radius_clause','show_count','terms_locked','ticket_price','extra']);
const OUTREACH_COLS = new Set(['ab_variant','email_open_count','email_open_last','last_email_sent','last_response','next_follow_up','next_sequence_date','sequence_day','sequence_status','thread_text','thread_url']);
const TOUR_COLS = new Set(['end_date','legacy_id','lodging_budget','misc_budget','name','notes','start_date','travel_budget','extra']);
const PERFORMER_COLS = new Set(['active','default_fee','legacy_id','name','notes','role','extra']);
const TEMPLATE_COLS = new Set(['body','legacy_id','name','photo_links','subject','tone','touch_number','venue_types','extra']);
const SHOW_COLS = new Set(['city','legacy_id','notes','show_date','state','status','extra']);
const CONTACT_COLS = new Set(['cares_about','email','first_name','is_primary','last_name','personal_details','personality_notes','phone','preferred_contact','proof_points']);
const LOG_COLS = new Set(['channel','direction','occurred_at','summary']);
const BOOKOUT_COLS = new Set(['start_date','end_date','reason']);

// Blob keys on venues that are stored in the deals table
const VENUE_DEAL_KEYS = new Set(Object.values(DEAL_COL_TO_KEY).concat(['guarantee','lodging','package','paid','notes'].map(k=>k)));
// note: deal 'notes' collides with venue 'notes'; venue notes wins the venues table, deal notes rides extra.
VENUE_DEAL_KEYS.delete('notes');
// Blob keys on venues stored in venue_outreach
const VENUE_OUTREACH_KEYS = new Set(Object.values(OUTREACH_COL_TO_KEY));
// Nested arrays on venues handled separately
const VENUE_NESTED = new Set(['contactLog', 'contacts']);

async function fetchAll(sb, table, select = '*') {
  const out = [];
  const page = 1000;
  for (let from = 0; ; from += page) {
    const { data, error } = await sb.from(table).select(select).range(from, from + page - 1);
    if (error) throw new Error(table + ': ' + error.message);
    out.push(...(data || []));
    if (!data || data.length < page) break;
  }
  return out;
}

// ---------- LOAD: tables -> blob ----------
export async function cloudFetch(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) {
    return { error: 'Bad email: ' + JSON.stringify(email) };
  }
  const sb = cloudClient();
  try {
    const [venues, contacts, logs, outreach, tours, shows, deals, performers, bookouts, templates, meta] =
      await Promise.all([
        fetchAll(sb, 'venues'), fetchAll(sb, 'venue_contacts'), fetchAll(sb, 'contact_log'),
        fetchAll(sb, 'venue_outreach'), fetchAll(sb, 'tours'), fetchAll(sb, 'shows'),
        fetchAll(sb, 'deals'), fetchAll(sb, 'performers'), fetchAll(sb, 'bookouts'),
        fetchAll(sb, 'templates'),
        sb.from('user_meta').select('updated_at').maybeSingle().then(r => r.data),
      ]);

    const byVenue = (rows) => {
      const m = {};
      for (const r of rows) (m[r.venue_id] = m[r.venue_id] || []).push(r);
      return m;
    };
    const contactsBy = byVenue(contacts);
    const logsBy = byVenue(logs);
    const outreachBy = byVenue(outreach);
    const dealsBy = byVenue(deals);
    const showsByTour = {};
    for (const s of shows) if (s.tour_id) (showsByTour[s.tour_id] = showsByTour[s.tour_id] || []).push(s);
    const bookoutsBy = {};
    for (const b of bookouts) (bookoutsBy[b.performer_id] = bookoutsBy[b.performer_id] || []).push(b);

    const blob = { venues: [], tours: [], comedians: [], templates: [] };

    for (const row of venues) {
      const hasExtra = row.extra && Object.keys(row.extra).length > 0;
      const v = rowToObj(row, VENUE_COL_TO_KEY);
      if (v.venue === undefined && v.name !== undefined) v.venue = v.name;
      // Repair fields that got saved as arrays but the app expects as strings
      for (const k of ['showDates','targetDates','preferredProofPoints','rebookDate','notes']) {
        if (Array.isArray(v[k])) v[k] = v[k].join(', ');
      }
      // baseline: any string-valued field seen in blob shape stays a string
      for (const k of Object.keys(v)) if (typeof v[k] === 'string' && v[k] == null) v[k] = '';
      // guarantee these string fields exist as empty strings if missing
      for (const k of ['venue','name','city','state','status','venueType','warmth','notes','address','zip','instagram','website','relationship','history','abVariant','emailThreadURL','emailThreadText','nextFollowUp','lastEmailSentDate','lastResponseDate','emailOpenLastDate','dealType','agreementType','contractStatus','contractUrl','doorSplit','expectedPaymentTiming','marketingSupport','merchAllowed','paidDate','radiusClause','dealClosedBy','priorFriction','referralSource','routingCluster','seasonalWindow','showDates','targetDates','preferredProofPoints','rebookDate']) {
        if (v[k] == null) v[k] = '';
      }
      // numeric fields used by Revenue Brain: default 0 not undefined
      for (const k of ['capacity','trustLevel','closeProbability','agentCommission','managerCommission','balanceDue','balancePaid','depositAmount','depositDue','depositPaid','guarantee','lodging','ticketPrice','merchCut','showCount','emailOpenCount','sequenceDay','philSplit','ticketsSold','websitePrice','estimatedGross']) {
        if (v[k] == null) v[k] = 0;
      }
      // array fields
      for (const k of ['contacts','contactLog','checklist','flightDetails']) {
        if (v[k] == null) v[k] = [];
      }
      // baseline: any string-valued field seen in blob shape stays a string
      for (const k of Object.keys(v)) if (typeof v[k] === 'string' && v[k] == null) v[k] = '';
      // guarantee these string fields exist as empty strings if missing
      for (const k of ['venue','name','city','state','status','venueType','warmth','notes','address','zip','instagram','website','relationship','history','abVariant','emailThreadURL','emailThreadText','nextFollowUp','lastEmailSentDate','lastResponseDate','emailOpenLastDate','dealType','agreementType','contractStatus','contractUrl','doorSplit','expectedPaymentTiming','marketingSupport','merchAllowed','paidDate','radiusClause','dealClosedBy','priorFriction','referralSource','routingCluster','seasonalWindow','showDates','targetDates','preferredProofPoints','rebookDate']) {
        if (v[k] == null) v[k] = '';
      }
      // numeric fields used by Revenue Brain: default 0 not undefined
      for (const k of ['capacity','trustLevel','closeProbability','agentCommission','managerCommission','balanceDue','balancePaid','depositAmount','depositDue','depositPaid','guarantee','lodging','ticketPrice','merchCut','showCount','emailOpenCount','sequenceDay','philSplit','ticketsSold','websitePrice','estimatedGross']) {
        if (v[k] == null) v[k] = 0;
      }
      // array fields
      for (const k of ['contacts','contactLog','checklist','flightDetails']) {
        if (v[k] == null) v[k] = [];
      }
      if (!hasExtra) {
        // reassemble from children for migrated (pre-adapter) rows
        const d = (dealsBy[row.id] || [])[0];
        if (d) Object.assign(v, rowToObj(d, DEAL_COL_TO_KEY));
        const o = (outreachBy[row.id] || [])[0];
        if (o) Object.assign(v, rowToObj(o, OUTREACH_COL_TO_KEY));
        const cl = logsBy[row.id];
        if (cl) v.contactLog = cl.map(r => rowToObj(r, {}));
        const cs = contactsBy[row.id];
        if (cs) v.contacts = cs.map(r => rowToObj(r, {}));
      }
      blob.venues.push(v);
    }
    for (const row of tours) {
      const _rowNull = (o) => {
        for (const k of ['name','notes','startDate','endDate']) if (o[k] == null) o[k] = '';
        for (const k of ['lodgingBudget','miscBudget','travelBudget']) if (o[k] == null) o[k] = 0;
      };
      const hasExtra = row.extra && Object.keys(row.extra).length > 0;
      const t = rowToObj(row, TOUR_COL_TO_KEY); _rowNull(t); _rowNull(t);
      if (!hasExtra) {
        const ds = showsByTour[row.id];
        if (ds) t.dates = ds.map(s => {
          const o = rowToObj(s, SHOW_COL_TO_KEY);
          for (const k of ['city','state','status','notes','date']) if (o[k] == null) o[k] = '';
          return o;
        });
      }
      blob.tours.push(t);
    }
    for (const row of performers) {
      const hasExtra = row.extra && Object.keys(row.extra).length > 0;
      const c = rowToObj(row, PERFORMER_COL_TO_KEY);
      if (!hasExtra) {
        const bs = bookoutsBy[row.id];
        if (bs) c.bookouts = bs.map(b => rowToObj(b, BOOKOUT_COL_TO_KEY));
      }
      blob.comedians.push(c);
    }
    for (const row of templates) {
      const t = rowToObj(row, TEMPLATE_COL_TO_KEY);
      if (t.subject == null) t.subject = '';
      if (t.body == null) t.body = '';
      if (t.name == null) t.name = '';
      if (t.tone == null) t.tone = '';
      blob.templates.push(t);
    }

    return { email, data: blob, updated_at: meta?.updated_at || null };
  } catch (e) {
    return { error: String(e.message || e) };
  }
}

// ---------- SAVE: blob -> tables ----------

// ─── USER PROFILE (user_meta.extra.profile) ───────────────────────────────
// Read/write the per-user identity used by fillTemplate merge fields.
export async function loadProfile() {
  const sb = cloudClient();
  if (!sb) return null;
  const { data: u } = await sb.auth.getUser();
  const uid = u?.user?.id;
  if (!uid) return null;
  const { data, error } = await sb.from('user_meta')
    .select('extra').eq('user_id', uid).maybeSingle();
  if (error) { console.warn('loadProfile:', error.message); return null; }
  return data?.extra?.profile || null;
}

export async function saveProfile(profile) {
  const sb = cloudClient();
  if (!sb) throw new Error('no client');
  const { data: u } = await sb.auth.getUser();
  const uid = u?.user?.id;
  if (!uid) throw new Error('no session');
  const { data: cur } = await sb.from('user_meta')
    .select('extra').eq('user_id', uid).maybeSingle();
  const extra = { ...(cur?.extra || {}), profile };
  const { error } = await sb.from('user_meta')
    .upsert({ user_id: uid, extra }, { onConflict: 'user_id' });
  if (error) throw new Error('saveProfile: ' + error.message);
  return profile;
}

let _saveInFlight = false;
export async function cloudSave(email, data) {
  if (_saveInFlight) { console.log('[SYNC] cloudSave skipped - save already in flight'); return { error: null, skipped: true }; }
  _saveInFlight = true;
  try { return await _cloudSaveInner(email, data); } finally { _saveInFlight = false; }
}
async function _cloudSaveInner(email, data) {
  if (!email || typeof email !== 'string' || !email.includes('@')) {
    return { error: 'Bad email: ' + JSON.stringify(email) };
  }
  if (!data || typeof data !== 'object') return { error: 'Bad data' };
  const sb = cloudClient();
  try {
    const { data: sess } = await sb.auth.getSession();
    const uid = sess?.session?.user?.id;
    if (!uid) return { error: 'Not signed in to StageBoss Cloud' };

    // wipe-and-rewrite (single-tenant-per-user scale; RLS scopes to own rows)
    const order = ['contact_log','venue_contacts','venue_outreach','deals','bookouts']; // upsert tables (venues/performers/templates) removed - never wiped
    for (const t of order) {
      const { error } = await sb.from(t).delete().eq('user_id', uid);
      if (error) throw new Error('clear ' + t + ': ' + error.message);
    }

    const venueRows = [], dealRows = [], outreachRows = [], contactRows = [], logRows = [];
    for (const v of (data.venues || [])) {
      const skip = new Set([...VENUE_DEAL_KEYS, ...VENUE_OUTREACH_KEYS, ...VENUE_NESTED, 'id']);
      const { cols, extra } = objToRow(v, VENUE_COLS, VENUE_COL_TO_KEY, skip);
      // keep full object in extra for lossless round-trip
      venueRows.push({ ...cols, user_id: uid, legacy_id: String(v.id ?? ''), extra: { ...extra, ...pick(v, [...VENUE_DEAL_KEYS, ...VENUE_OUTREACH_KEYS, ...VENUE_NESTED]) } });
    }
    const { data: insertedVenues, error: ve } = await chunkInsert(sb, 'venues', venueRows, 'id,legacy_id', 'user_id,legacy_id');
    if (ve) throw new Error('venues: ' + ve);
    const vidByLegacy = {};
    for (const r of insertedVenues) vidByLegacy[r.legacy_id] = r.id;

    for (const v of (data.venues || [])) {
      const vid = vidByLegacy[String(v.id ?? '')];
      if (!vid) continue;
      const dealObj = pick(v, VENUE_DEAL_KEYS);
      if (Object.keys(dealObj).length) {
        const { cols, extra } = objToRow(dealObj, DEAL_COLS, DEAL_COL_TO_KEY);
        dealRows.push({ ...cols, extra, user_id: uid, venue_id: vid });
      }
      const outObj = pick(v, VENUE_OUTREACH_KEYS);
      if (Object.keys(outObj).length) {
        const { cols } = objToRow(outObj, OUTREACH_COLS, OUTREACH_COL_TO_KEY);
        outreachRows.push({ ...cols, user_id: uid, venue_id: vid });
      }
      for (const c of (v.contacts || [])) {
        const { cols } = objToRow(c, CONTACT_COLS, {}, new Set(['id']));
        contactRows.push({ ...cols, user_id: uid, venue_id: vid });
      }
      for (const l of (v.contactLog || [])) {
        const { cols } = objToRow(l, LOG_COLS, {}, new Set(['id']));
        logRows.push({ ...cols, user_id: uid, venue_id: vid });
      }
    }
    let r;
    r = await chunkInsert(sb, 'deals', dealRows); if (r.error) throw new Error('deals: ' + r.error);
    r = await chunkInsert(sb, 'venue_outreach', outreachRows); if (r.error) throw new Error('venue_outreach: ' + r.error);
    r = await chunkInsert(sb, 'venue_contacts', contactRows); if (r.error) throw new Error('venue_contacts: ' + r.error);
    r = await chunkInsert(sb, 'contact_log', logRows); if (r.error) throw new Error('contact_log: ' + r.error);

    const tourRows = [];
    for (const t of (data.tours || [])) {
      const { cols, extra } = objToRow(t, TOUR_COLS, TOUR_COL_TO_KEY, new Set(['dates', 'id']));
      tourRows.push({ ...cols, user_id: uid, legacy_id: String(t.id ?? ''), extra: { ...extra, ...pick(t, ['dates']) } });
    }
    const { data: insertedTours, error: te } = { data: [], error: null }; // tours read-only: owned by migration, saveAll must not touch
    if (te) throw new Error('tours: ' + te);
    const tidByLegacy = {};
    for (const x of insertedTours) tidByLegacy[x.legacy_id] = x.id;
    const showRows = [];
    for (const t of (data.tours || [])) {
      const tid = tidByLegacy[String(t.id ?? '')];
      for (const s of (t.dates || [])) {
        const { cols, extra } = objToRow(s, SHOW_COLS, SHOW_COL_TO_KEY, new Set(['id']));
        showRows.push({ ...cols, extra, user_id: uid, tour_id: tid || null });
      }
    }
    // shows read-only: owned by migration, saveAll must not touch

    const perfRows = [];
    for (const c of (data.comedians || [])) {
      const { cols, extra } = objToRow(c, PERFORMER_COLS, PERFORMER_COL_TO_KEY, new Set(['bookouts', 'id']));
      perfRows.push({ ...cols, user_id: uid, legacy_id: String(c.id ?? ''), extra: { ...extra, ...pick(c, ['bookouts']) } });
    }
    const { data: insertedPerfs, error: pe } = await chunkInsert(sb, 'performers', perfRows, 'id,legacy_id', 'user_id,legacy_id');
    if (pe) throw new Error('performers: ' + pe);
    const pidByLegacy = {};
    for (const x of insertedPerfs) pidByLegacy[x.legacy_id] = x.id;
    const bookoutRows = [];
    for (const c of (data.comedians || [])) {
      const pid = pidByLegacy[String(c.id ?? '')];
      for (const b of (c.bookouts || [])) {
        const { cols } = objToRow(b, BOOKOUT_COLS, BOOKOUT_COL_TO_KEY, new Set(['id']));
        bookoutRows.push({ ...cols, user_id: uid, performer_id: pid || null });
      }
    }
    r = await chunkInsert(sb, 'bookouts', bookoutRows); if (r.error) throw new Error('bookouts: ' + r.error);

    const tmplRows = [];
    for (const t of (data.templates || [])) {
      const { cols, extra } = objToRow(t, TEMPLATE_COLS, TEMPLATE_COL_TO_KEY, new Set(['id']));
      tmplRows.push({ ...cols, extra, user_id: uid, legacy_id: String(t.id ?? '') });
    }
    r = await chunkInsert(sb, 'templates', tmplRows, 'id,legacy_id', 'user_id,legacy_id'); if (r.error) throw new Error('templates: ' + r.error);

    const now = new Date().toISOString();
    const { error: me } = await sb.from('user_meta').upsert({ user_id: uid, updated_at: now });
    if (me) throw new Error('user_meta: ' + me.message);

    return { email, updated_at: now };
  } catch (e) {
    return { error: String(e.message || e) };
  }
}

function pick(obj, keys) {
  const out = {};
  for (const k of keys) if (obj[k] !== undefined) out[k] = obj[k];
  return out;
}

async function chunkInsert(sb, table, rows, select, conflict) {
  const out = [];
  for (let i = 0; i < rows.length; i += 500) {
    const slice = rows.slice(i, i + 500);
    if (!slice.length) break;
    let q = conflict
      ? sb.from(table).upsert(slice, { onConflict: conflict, ignoreDuplicates: false })
      : sb.from(table).insert(slice);
    if (select) q = q.select(select);
    const { data, error } = await q;
    if (error) return { error: error.message };
    if (data) out.push(...data);
  }
  return { data: out, error: null };
}
